import { useEffect, useState } from 'react'
import { Icon } from '../ui/Icon'
import { manageSubscription, purchaseLane } from './checkout'
import {
  getBilling,
  isPaidPlan,
  minutesOf,
  refreshBilling,
  resetsIn,
  useBilling,
  type Plan,
  type PlanName,
  type Usage,
} from './plan'
import { PlusSheet } from './PlusSheet'
import { useClock } from './useClock'

/**
 * The main menu's ADVANCED FEATURE section, whose one row today is voice input —
 * owner, 2026-09-09: 使用者的設定頁面 needs to show 還有多久 語音功能會 refresh. One row:
 * which plan, what is left today, when it comes back, and the one button that changes
 * it (Upgrade on free, Manage on Plus).
 *
 * The section is named for what it will hold, the row for what it is (owner,
 * 2026-09-11: 以後 Advanced Feature 下面會有許多不同的欄位供使用者購買, 現在先提供第一種).
 * So the heading is generic and every headline starts with the feature's name.
 *
 * The free tier is described as "limited" and never as a number of minutes (owner's
 * addendum): the count a person sees is the hour they would get by upgrading. Plus
 * sees its minutes because those are what it paid for.
 *
 * Reads when the menu opens (mount) — never on a timer; the countdown is a clock.
 */
export function VoiceSection() {
  const { plan, usage } = useBilling()
  const now = useClock()
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [paywall, setPaywall] = useState(false)

  useEffect(() => {
    void refreshBilling()
  }, [])

  const lane = purchaseLane()
  const isPaid = isPaidPlan(plan?.plan)
  /* Plus given by the account owner, with no purchase behind it (owner, 2026-09-13).
     There is nothing to manage and nothing to buy, so the row states the fact and
     offers no button — "Manage" would open a store that has never heard of this
     account, and "Upgrade" would sell something already held. */
  const comped = plan?.comped === true

  const act = async () => {
    setNote(null)
    if (!(isPaid && plan)) {
      // Upgrade opens the paywall (PlusSheet) — the disclosure first, the till from there.
      setPaywall(true)
      return
    }
    setBusy(true)
    try {
      if (!(await manageSubscription(plan))) setNote('Manage your subscription from the store you bought it in.')
    } finally {
      setBusy(false)
    }
  }

  const bought = () => {
    setPaywall(false)
    // The till took the payment but the plan row has not flipped within the settle
    // window (checkout.ts): say so, rather than leave "Free" over a receipt.
    if (!isPaidPlan(getBilling().plan?.plan)) setNote('Payment received. Plus will appear here in a moment.')
  }

  return (
    <>
      {paywall && <PlusSheet onClose={() => setPaywall(false)} onDone={bought} />}
      <div className="gh-sec">Advanced Feature</div>
      <div className="voice-row" data-plan={plan?.plan ?? 'free'}>
        <span className="voice-ic" aria-hidden="true">
          <Icon name="mic" size={18} label="" />
        </span>
        <div className="voice-who">
          <span className="voice-plan">{headline(plan, usage)}</span>
          <span className="voice-sub">{subline(plan, usage, now)}</span>
        </div>
        {(isPaid || lane !== 'none') && !comped && (
          <button type="button" className="gh-mini" onClick={() => void act()} disabled={busy}>
            {busy ? '…' : isPaid ? 'Manage' : 'Upgrade'}
          </button>
        )}
      </div>
      {note && <p className="gh-note">{note}</p>}
    </>
  )
}

/** The row names its feature, because the section heading no longer does. */
const FEATURE = 'Voice Input'

/** "Voice Input: Free · limited" / "Voice Input: Plus · 42 min left today" / "…Plus · today's hour used". */
/**
 * The tier's own name, capitalised — never a name this file chose.
 *
 * Owner, 2026-09-17: 這一個上限的名稱不應該叫做 Plus … 可以把它叫成 Ultra. The row used
 * to print the literal "Plus" whatever the server said, so a new tier could only ever
 * appear under the old one's name — which is how a 24-hour allowance came to be
 * described as a Plus hour on his screen.
 */
const tierName = (p: PlanName): string => p.charAt(0).toUpperCase() + p.slice(1)

function headline(plan: Plan | null, usage: Usage | null): string {
  const p = plan?.plan ?? 'free'
  if (!isPaidPlan(p)) return `${FEATURE}: Free · limited`
  if (!usage) return `${FEATURE}: ${tierName(p)}`
  // "today's hour" was true of exactly one tier; say what is actually gone.
  if (usage.remainingS <= 0) return `${FEATURE}: ${tierName(p)} · today’s allowance used`
  return `${FEATURE}: ${tierName(p)} · ${minutesOf(usage.remainingS)} min left today`
}

/** When the allowance returns, and — for Plus — what the subscription is doing. */
function subline(plan: Plan | null, usage: Usage | null, now: number): string {
  const parts: string[] = []
  if (usage) {
    parts.push(usage.remainingS <= 0 ? `Resets ${resetsIn(usage.resetsAt, now)}` : `Allowance resets ${resetsIn(usage.resetsAt, now)}`)
  } else {
    parts.push('Allowance resets at midnight UTC')
  }
  if (plan?.plan === 'plus') {
    if (plan.status === 'grace') parts.push('payment problem — update your card to keep Plus')
    else if (plan.expiresAt) parts.push(`${plan.renews ? 'Renews' : 'Ends'} ${dateOf(plan.expiresAt)}`)
    // No date and nothing to renew: say WHY it is Plus, or the line just stops.
    else if (plan.comped) parts.push('Included with your account')
  } else if (plan?.status === 'expired') {
    parts.push('Plus ended')
  }
  return parts.join(' · ')
}

/**
 * "Sep 9" — or "today" / "tomorrow" when that is what the date is. A renewal
 * date equal to today reads as an alarm to a person ("Renews Sep 9" on Sep 9,
 * avg-app-builder from the owner's phone, 2026-09-10); saying "today" is the same
 * fact stated plainly. In production a monthly renewal lands there only on its
 * last day; in the sandbox, where a month is five minutes, it is the usual case.
 */
function dateOf(unixS: number, now = Date.now()): string {
  try {
    const d = new Date(unixS * 1000)
    const sameDay = (a: Date, b: Date) =>
      a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
    const today = new Date(now)
    if (sameDay(d, today)) return 'today'
    const tomorrow = new Date(now + 86_400_000)
    if (sameDay(d, tomorrow)) return 'tomorrow'
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return ''
  }
}
